import { useEffect, useState } from 'react';
import { isPdfNotesSupported } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getSupplierSummary } from '../../../../../redux/services/suppliersService';
import { SupplierUsersSection } from './SupplierUsersSection/SupplierUsersSection';
import { AdminSupplierEmailsSection } from './AdminSupplierEmailsSection';
import { StatementsSettingsSection } from './StatementsSettingsSection/StatementsSettingsSection';
import { InvoicesSettingsSection } from './InvoicesSettingsSection/InvoicesSettingsSection';
import { InvoicePaymentReminderSettingsSection } from './InvoicePaymentReminderSettingsSection/InvoicePaymentReminderSettingsSection';
import { PhoneNumbersSettingsSection } from './PhoneNumbersSettingsSection/PhoneNumbersSettingsSection';
import { AiAutomationSettingsSection } from './AiAutomationSettingsSection/AiAutomationSettingsSection';
import { CreditCardFeeWaiverSettingsSection } from './CreditCardFeeWaiverSettingsSection/CreditCardFeeWaiverSettingsSection';
import { DiscountSettingsSection } from './DiscountSettingsSection/DiscountSettingsSection';
import { FiservBuyerFeeRateSettingsSection } from './FiservBuyerFeeRateSettingsSection/FiservBuyerFeeRateSettingsSection';
import { DisabledPaymentTypesSettingsSection } from './DisabledPaymentTypesSettingsSection/DisabledPaymentTypesSettingsSection';
import { CreditMemoSettingsSection } from './CreditMemoSettings/CreditMemoSettings';
import { APP_ENVIRONMENT } from '../../../../../utils/constants';
import BranchUserManager from './BranchSettings';
import TradeReferenceSettings from './TradeReferenceSettings';
import { CreditApplicationSettingsSection } from './CreditApplicationSettingsSection';
import { CreditReportSettingsSection } from './CreditReportSettingsSection';
import { SalesRepsSettingsSection } from './SalesRepsSettingsSection';
import { TaxExemptionTeamSettingsSection } from './TaxExemptionTeamSettingsSection';

type Tab = 'users' | 'ar' | 'credit';

const TABS: { id: Tab; label: string }[] = [
  { id: 'users', label: 'Users Settings' },
  { id: 'ar', label: 'AR Settings' },
  { id: 'credit', label: 'Credit Application Settings' },
];

function SupplierSettings() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplier = currentSupplier;
  // The inbox OAuth sign-in returns here with ?inboxConnect=…; reopen the tab it started from.
  const [activeTab, setActiveTab] = useState<Tab>(() =>
    new URLSearchParams(window.location.search).has('inboxConnect') ? 'ar' : 'users'
  );
  const isFiserv = APP_ENVIRONMENT !== 'production' || supplier?.cardPaymentProvider === 'FISERV';

  useEffect(() => {
    if (user?.id && currentSupplier?.id) {
      dispatch(getSupplierSummary({ supplierId: currentSupplier?.id || '' }));
    }

    return () => {};
  }, [user?.id, currentSupplier?.id]);

  return (
    <div className="flex flex-col w-full h-full ">
      {/* Tab bar */}
      <div className="sticky px-4 top-0 bg-white z-30 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex flex-col w-full h-full p-4 overflow-scroll">
        {activeTab === 'users' && (
          <div className="flex flex-col gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <SupplierUsersSection supplier={supplier} />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <AdminSupplierEmailsSection supplier={supplier} />
            </div>
          </div>
        )}

        {activeTab === 'ar' && (
          <div className="grid grid-cols-2 grid-rows-2 gap-4">
            <div className="flex flex-col gap-4">
              {/* Statements Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                <StatementsSettingsSection supplier={supplier} />
              </div>

              {/* Invoices Settings Section - only suppliers whose invoice PDF has a notes box */}
              {isPdfNotesSupported(supplier) && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                  <InvoicesSettingsSection supplier={supplier} />
                </div>
              )}

              {/* Discount Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                <DiscountSettingsSection supplier={supplier} />
              </div>

              {/* Disable Payment Methods Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                <DisabledPaymentTypesSettingsSection supplier={supplier} />
              </div>

              {/* Sales Reps Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                <SalesRepsSettingsSection supplierId={supplier?.id || ''} />
              </div>

              {/* AI Automation + Contact Information Settings Section */}
              {(APP_ENVIRONMENT !== 'production' ||
                supplier?.isAiDocumentRequestResponseEnabled === true) && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                  <AiAutomationSettingsSection supplier={supplier} />
                  {supplier?.isAiDocumentRequestResponseEnabled === true && (
                    <div className="mt-5 pt-5 border-t border-gray-200">
                      <PhoneNumbersSettingsSection supplier={supplier} />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4">
              {/* Invoice Payment Reminder Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                <InvoicePaymentReminderSettingsSection supplier={supplier} />
              </div>
              {/* Aged Credit Memos Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <CreditMemoSettingsSection supplier={supplier} />
              </div>

              {/* Card Fee Rate Settings Section */}
              {isFiserv && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                  <FiservBuyerFeeRateSettingsSection supplier={supplier} />
                </div>
              )}

              {/* Credit Card Fee Waiver Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                <CreditCardFeeWaiverSettingsSection supplier={supplier} />
              </div>

              {/* Tax Exemption Team Settings Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 h-fit">
                <TaxExemptionTeamSettingsSection supplierId={supplier?.id || ''} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'credit' && (
          <div className="flex flex-col gap-4">
            <TradeReferenceSettings supplier={supplier} />
            <div className="bg-white border border-gray-200 rounded-xl">
              <BranchUserManager supplier={supplier} />
            </div>
            <CreditApplicationSettingsSection supplier={supplier} />
            <CreditReportSettingsSection supplier={supplier} />
          </div>
        )}
      </div>
    </div>
  );
}

export default SupplierSettings;
